import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown, BookOpen, HelpCircle } from 'lucide-react';
import LanguageSwitcher from '../LanguageSwitcher';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3885f60a-e4ce-4e7e-b71d-89edd5337d92/artifacts/2dlizjmm_Gemini_Generated_Image_1lmywa1lmywa1lmy.jpeg';

const LandingNav = ({ onLoginClick, onRegisterClick }) => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const resourcesRef = useRef(null);

  const closeMobile = () => setMobileOpen(false);

  // Close resources dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (resourcesRef.current && !resourcesRef.current.contains(e.target)) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-slate-200 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2" onClick={closeMobile}>
            <img
              src={LOGO_URL}
              alt="ProposAI logo — AI-powered RFP platform for Canadian businesses"
              className="w-10 h-10 rounded-lg object-contain bg-white"
            />
            <span className="text-2xl font-bold text-primary">ProposAI</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-6">
            <a href="#features" className="text-slate-600 hover:text-primary transition-colors font-medium text-sm">
              {t('landing.nav.features')}
            </a>
            <a href="#pricing" className="text-slate-600 hover:text-primary transition-colors font-medium text-sm">
              {t('landing.nav.pricing')}
            </a>

            {/* Resources dropdown */}
            <div className="relative" ref={resourcesRef}>
              <button
                onClick={() => setResourcesOpen(o => !o)}
                className="flex items-center gap-1 text-slate-600 hover:text-primary transition-colors font-medium text-sm"
                data-testid="nav-resources-btn"
              >
                Resources
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              {resourcesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-52 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50">
                    <Link
                    to="/faq"
                    onClick={() => setResourcesOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-primary/5 hover:text-primary transition-colors"
                    data-testid="nav-faq-link"
                  >
                    <HelpCircle className="w-4 h-4 text-primary/70" />
                    <div>
                      <div className="font-medium">FAQ</div>
                      <div className="text-xs text-slate-500">27 answers</div>
                    </div>
                  </Link>
                  <div className="border-t border-slate-100" />
                  <Link
                    to="/resources"
                    onClick={() => setResourcesOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-primary/5 hover:text-primary transition-colors"
                    data-testid="nav-resources-link"
                  >
                    <BookOpen className="w-4 h-4 text-primary/70" />
                    <div>
                      <div className="font-medium">Procurement Guides</div>
                      <div className="text-xs text-slate-500">6 expert articles</div>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            <LanguageSwitcher />
            <Button
              variant="ghost"
              onClick={onLoginClick}
              className="text-slate-600 hover:text-primary text-sm"
              data-testid="nav-signin-btn"
            >
              {t('landing.nav.signIn')}
            </Button>
            <Button
              onClick={onRegisterClick}
              className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-stripe text-sm"
              data-testid="nav-get-started-btn"
            >
              {t('landing.nav.getStarted')}
            </Button>
          </div>

          {/* Mobile: hamburger + language switcher */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileOpen(o => !o)}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-nav-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            <a
              href="#features"
              onClick={closeMobile}
              className="px-3 py-2.5 text-slate-700 hover:text-primary hover:bg-slate-50 rounded-lg font-medium transition-colors text-sm"
            >
              {t('landing.nav.features')}
            </a>
            <a
              href="#pricing"
              onClick={closeMobile}
              className="px-3 py-2.5 text-slate-700 hover:text-primary hover:bg-slate-50 rounded-lg font-medium transition-colors text-sm"
            >
              {t('landing.nav.pricing')}
            </a>
            <Link
              to="/faq"
              onClick={closeMobile}
              className="px-3 py-2.5 text-slate-700 hover:text-primary hover:bg-slate-50 rounded-lg font-medium transition-colors text-sm flex items-center gap-2"
            >
              <HelpCircle className="w-4 h-4" /> FAQ
            </Link>
            <Link
              to="/resources"
              onClick={closeMobile}
              className="px-3 py-2.5 text-slate-700 hover:text-primary hover:bg-slate-50 rounded-lg font-medium transition-colors text-sm flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4" /> Procurement Guides
            </Link>
            <div className="border-t border-slate-100 my-2" />
            <Button
              variant="ghost"
              onClick={() => { closeMobile(); onLoginClick(); }}
              className="justify-start text-slate-700 hover:text-primary px-3 text-sm"
              data-testid="mobile-nav-signin-btn"
            >
              {t('landing.nav.signIn')}
            </Button>
            <Button
              onClick={() => { closeMobile(); onRegisterClick(); }}
              className="bg-primary hover:bg-primary/90 text-primary-foreground text-sm"
              data-testid="mobile-nav-get-started-btn"
            >
              {t('landing.nav.getStarted')}
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default LandingNav;
